import { mount, unmount, type Component } from 'svelte';
import QuickSearch from '$lib/components/QuickSearch.svelte';
import QuickRoomPrice from '$lib/components/QuickRoomPrice.svelte';
import type { RoomConfig, WidgetConfig } from '$lib/types';
import { primaryColorVars } from '$lib/utils';
import { seedSelectionDefaults, saveRedirectUrl } from '$lib/storage';
import { buildRedirectUrl } from '$lib/api';
import rawStyles from './app.css?inline';

declare const __APP_VERSION__: string;

console.log(
  `%c QuickSearch v${__APP_VERSION__} %c https://www.eliophot.com `,
  'background:#0f172a;color:#fff;padding:2px 6px;border-radius:3px 0 0 3px;font-weight:bold',
  'background:#3b82f6;color:#fff;padding:2px 6px;border-radius:0 3px 3px 0',
);

/**
 * Shadow DOM inherits rem from the host page's <html> font-size, not from the
 * shadow root. Convert all rem values to px (1 rem = 16 px) so the widget
 * renders consistently regardless of the host page's root font-size.
 */
const styles = rawStyles.replace(
  /(\d+\.?\d*|\.\d+)rem/g,
  (_, v) => `${Math.round(parseFloat(v) * 16 * 1000) / 1000}px`,
);

type MountOptions = Partial<WidgetConfig> & {
  styleOverrides?: string;
};

type RoomMountOptions = MountOptions & {
  idRoom?: string;
  roomtcode?: string;
  catcode?: string;
  label?: string;
};

type VisualOptions = {
  primaryColor?: string;
  borderRadius?: string;
  showShadow?: boolean;
  backgroundColor?: string;
  showBorder?: boolean;
  borderColor?: string;
  styleOverrides?: string;
};

type Instance = {
  update: (opts: VisualOptions) => void;
  destroy: () => void;
};

const DEFAULT_API_URL = 'https://api-next.eliophot.com';
const DEFAULT_LANG = 'fr-FR';

function readWidgetConfig(
  el: HTMLElement,
  overrides?: MountOptions,
): WidgetConfig {
  const apiUrl = overrides?.apiUrl ?? el.dataset.apiUrl ?? DEFAULT_API_URL;
  const rawToken = overrides?.token ?? el.dataset.token ?? '';
  const token = rawToken ? atob(rawToken) : '';
  const lang = overrides?.lang ?? el.dataset.lang ?? DEFAULT_LANG;
  const primaryColor =
    overrides?.primaryColor ?? el.dataset.primaryColor ?? undefined;
  const borderRadius =
    overrides?.borderRadius ?? el.dataset.borderRadius ?? undefined;
  const showShadowRaw = overrides?.showShadow ?? el.dataset.showShadow;
  const showShadow =
    showShadowRaw === undefined ? true : String(showShadowRaw) !== 'false';
  const backgroundColor =
    overrides?.backgroundColor ?? el.dataset.backgroundColor ?? undefined;
  const showBorderRaw = overrides?.showBorder ?? el.dataset.showBorder;
  const showBorder =
    showBorderRaw === undefined ? true : String(showBorderRaw) !== 'false';
  const borderColor =
    overrides?.borderColor ?? el.dataset.borderColor ?? undefined;
  const separateDatesRaw =
    overrides?.separateDates ?? el.dataset.separateDates;
  const separateDates =
    separateDatesRaw === undefined
      ? false
      : String(separateDatesRaw) === 'true';
  const showBookButtonRaw =
    overrides?.showBookButton ?? el.dataset.showBookButton;
  const showBookButton =
    showBookButtonRaw === undefined
      ? true
      : String(showBookButtonRaw) !== 'false';
  const showPricingRaw = overrides?.showPricing ?? el.dataset.showPricing;
  const showPricing =
    showPricingRaw === undefined ? true : String(showPricingRaw) !== 'false';
  return {
    apiUrl: apiUrl.replace(/\/$/, ''),
    token,
    lang,
    primaryColor,
    borderRadius,
    showShadow,
    backgroundColor,
    showBorder,
    borderColor,
    separateDates,
    showBookButton,
    showPricing,
  };
}

function readRoomConfig(
  el: HTMLElement,
  overrides?: RoomMountOptions,
): RoomConfig {
  return {
    id: el.id || crypto.randomUUID(),
    label: overrides?.label ?? el.dataset.label ?? undefined,
    idRoom: overrides?.idRoom ?? el.dataset.idRoom ?? undefined,
    roomtcode: overrides?.roomtcode ?? el.dataset.roomtcode ?? undefined,
    catcode: overrides?.catcode ?? el.dataset.catcode ?? undefined,
  };
}

type RootHandle = {
  shadow: ShadowRoot;
  container: HTMLElement;
  colorStyleEl: HTMLStyleElement;
  radiusStyleEl: HTMLStyleElement;
  shadowStyleEl: HTMLStyleElement;
  bgStyleEl: HTMLStyleElement;
  borderStyleEl: HTMLStyleElement;
  customStyleEl: HTMLStyleElement;
};

function readStyleOverrides(target: HTMLElement, options?: MountOptions): string {
  if (options?.styleOverrides) return options.styleOverrides;
  const inlineStyle = target.querySelector('style');
  if (inlineStyle) return inlineStyle.textContent || '';
  return target.dataset.styleOverrides || '';
}

function attachRoot(target: HTMLElement, config: WidgetConfig, styleOverrides?: string): RootHandle {
  const shadow = target.attachShadow({ mode: 'open' });

  const styleEl = document.createElement('style');
  styleEl.textContent = styles;
  shadow.appendChild(styleEl);

  const colorStyleEl = document.createElement('style');
  const colorOverrides = primaryColorVars(config.primaryColor);
  if (colorOverrides) {
    colorStyleEl.textContent = `.quick-search-root, .quick-search-root * { ${colorOverrides} }`;
  }
  shadow.appendChild(colorStyleEl);

  const radiusStyleEl = document.createElement('style');
  if (config.borderRadius) {
    radiusStyleEl.textContent = `.quick-search-root { --radius: ${config.borderRadius}; }`;
  }
  shadow.appendChild(radiusStyleEl);

  const innerSel = '.quick-search-root > .quick-search-root';

  const shadowStyleEl = document.createElement('style');
  if (config.showShadow === false) {
    shadowStyleEl.textContent = `${innerSel} { box-shadow: none !important; }`;
  } else if (config.borderColor) {
    shadowStyleEl.textContent = `${innerSel} { box-shadow: 0 1px 3px ${config.borderColor}40, 0 1px 2px ${config.borderColor}30 !important; }`;
  }
  shadow.appendChild(shadowStyleEl);

  const bgStyleEl = document.createElement('style');
  if (config.backgroundColor) {
    bgStyleEl.textContent = `${innerSel} { background-color: ${config.backgroundColor} !important; }`;
  }
  shadow.appendChild(bgStyleEl);

  const borderStyleEl = document.createElement('style');
  const borderRules: string[] = [];
  if (config.showBorder === false) {
    borderRules.push('border: none !important');
  }
  if (config.borderColor) {
    borderRules.push(`border-color: ${config.borderColor} !important`);
  }
  if (borderRules.length) {
    borderStyleEl.textContent = `${innerSel} { ${borderRules.join('; ')}; }`;
  }
  shadow.appendChild(borderStyleEl);

  const customStyleEl = document.createElement('style');
  if (styleOverrides) {
    customStyleEl.textContent = styleOverrides;
  }
  shadow.appendChild(customStyleEl);

  const container = document.createElement('div');
  container.className = 'quick-search-root';
  shadow.appendChild(container);

  return { shadow, container, colorStyleEl, radiusStyleEl, shadowStyleEl, bgStyleEl, borderStyleEl, customStyleEl };
}

function mountWidget<Props extends Record<string, unknown>>(
  target: HTMLElement,
  options: MountOptions | undefined,
  flag: string,
  Comp: Component<Props>,
  buildProps: (config: WidgetConfig, root: RootHandle) => Props,
): Instance {
  if (target.dataset[flag] === '1') {
    return { update: () => {}, destroy: () => {} };
  }
  target.dataset[flag] = '1';

  const config = readWidgetConfig(target, options);
  const overridesCss = readStyleOverrides(target, options);
  const root = attachRoot(target, config, overridesCss);
  const app = mount(Comp, {
    target: root.container,
    props: buildProps(config, root),
  });

  return {
    update: (opts: VisualOptions) => {
      const inner = '.quick-search-root > .quick-search-root';
      const vars = primaryColorVars(opts.primaryColor);
      root.colorStyleEl.textContent = vars
        ? `.quick-search-root, .quick-search-root * { ${vars} }`
        : '';
      root.radiusStyleEl.textContent = opts.borderRadius
        ? `.quick-search-root { --radius: ${opts.borderRadius}; }`
        : '';
      if (opts.showShadow === false) {
        root.shadowStyleEl.textContent = `${inner} { box-shadow: none !important; }`;
      } else if (opts.borderColor) {
        root.shadowStyleEl.textContent = `${inner} { box-shadow: 0 1px 3px ${opts.borderColor}40, 0 1px 2px ${opts.borderColor}30 !important; }`;
      } else {
        root.shadowStyleEl.textContent = '';
      }
      root.bgStyleEl.textContent = opts.backgroundColor
        ? `${inner} { background-color: ${opts.backgroundColor} !important; }`
        : '';
      const borderRules: string[] = [];
      if (opts.showBorder === false) {
        borderRules.push('border: none !important');
      }
      if (opts.borderColor) {
        borderRules.push(`border-color: ${opts.borderColor} !important`);
      }
      root.borderStyleEl.textContent = borderRules.length
        ? `${inner} { ${borderRules.join('; ')}; }`
        : '';
      if (opts.styleOverrides !== undefined) {
        root.customStyleEl.textContent = opts.styleOverrides;
      }
    },
    destroy: () => {
      unmount(app);
      delete target.dataset[flag];
      target.innerHTML = '';
    },
  };
}

export function mountQuickSearch(
  target: HTMLElement,
  options?: MountOptions,
): Instance {
  const config = readWidgetConfig(target, options);
  const selection = seedSelectionDefaults();
  saveRedirectUrl(
    buildRedirectUrl(config, {
      startdate: selection.checkin,
      enddate: selection.checkout,
      residents: selection.guests,
    }),
  );

  return mountWidget(
    target,
    options,
    'quickSearchMounted',
    QuickSearch as unknown as Component<Record<string, unknown>>,
    (_config, root) => ({
      config,
      portalTarget: root.container,
    }),
  );
}

export function mountQuickRoomPrice(
  target: HTMLElement,
  options?: RoomMountOptions,
): Instance {
  return mountWidget(
    target,
    options,
    'quickRoomPriceMounted',
    QuickRoomPrice as unknown as Component<Record<string, unknown>>,
    (config) => ({
      config,
      room: readRoomConfig(target, options),
    }),
  );
}

function autoMount() {
  document
    .querySelectorAll<HTMLElement>('[data-quick-search]')
    .forEach((node) => {
      mountQuickSearch(node);
    });
  document
    .querySelectorAll<HTMLElement>('[data-quick-room-price]')
    .forEach((node) => {
      mountQuickRoomPrice(node);
    });
}

declare global {
  interface Window {
    QuickSearch?: {
      mount: typeof mountQuickSearch;
      mountRoom: typeof mountQuickRoomPrice;
    };
  }
}

if (typeof window !== 'undefined') {
  window.QuickSearch = {
    mount: mountQuickSearch,
    mountRoom: mountQuickRoomPrice,
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoMount, { once: true });
  } else {
    autoMount();
  }
}

if (import.meta.hot) {
  import.meta.hot.accept((mod) => {
    if (typeof window === 'undefined') return;
    if (mod?.mountQuickSearch && mod?.mountQuickRoomPrice) {
      window.QuickSearch = {
        mount: mod.mountQuickSearch,
        mountRoom: mod.mountQuickRoomPrice,
      };
    }
  });
}

export { mountQuickSearch as mount, mountQuickRoomPrice as mountRoom };
